"use client";
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    FaUsers,
    FaShoppingCart,
    FaBoxOpen,
    FaMoneyBillWave,
} from 'react-icons/fa';
import { Eye } from "lucide-react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import './style.css';
import { ProductContext } from "../../../contexts/AdminProductContext";
import Loading from "../../../components/Loading/Loading";
import PageWrapper  from "../../../components/pageWrapper/PageWrapper";

const addSmoothScroll = () => {
    const style = document.createElement('style');
    style.innerHTML = `
        html {
            scroll-behavior: smooth;
        }
    `;
    document.head.appendChild(style);
};

const Dashboard = () => {
    const { reports, fetchReport } = useContext(ProductContext);
    const navigate = useNavigate();
    const [salesData, setSalesData] = useState([]);
    const [productData, setProductData] = useState([]);
    const [categoryData, setCategoryData] = useState([]);
    const [recentOrders, setRecentOrders] = useState([]);
    const [stats, setStats] = useState({
        totalCustomers: 0,
        totalOrders: 0,
        totalProducts: 0,
        totalRevenue: 0
    });

    // State để lưu màu ngẫu nhiên cho mỗi biểu đồ
    const [salesChartColor, setSalesChartColor] = useState('');
    const [productChartColor, setProductChartColor] = useState('');

    // Loại bỏ màu xám, đen, trắng, chỉ giữ các màu tươi sáng
    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#EF4444', '#10B981', '#F97316', '#EC4899', '#8B5CF6', '#14B8A6'];

    // Thêm hiệu ứng cuộn mượt mà khi component mount
    useEffect(() => {
        addSmoothScroll();
    }, []);

    // Chọn màu ngẫu nhiên một lần duy nhất khi component mount
    useEffect(() => {
        const randomSalesColor = COLORS[Math.floor(Math.random() * COLORS.length)];
        setSalesChartColor(randomSalesColor);

        const randomProductColor = COLORS[Math.floor(Math.random() * COLORS.length)];
        setProductChartColor(randomProductColor);
    }, []); // Dependency rỗng để chỉ chạy một lần khi component mount

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    useEffect(() => {
        if (reports !== null) {
            const revenue = reports.revenues || [];
            const topSaleProduct = reports.topSaleproduct || [];
            const orders = reports.orders || [];

            setStats({
                totalCustomers: reports.totalCustomer,
                totalOrders: reports.totalOrder,
                totalProducts: reports.totalProductSale,
                totalRevenue: revenue.reduce((sum, r) => sum + r.amount, 0),
            });

            setSalesData(revenue.map(revenue => ({
                name: `T${revenue.month}`,
                sales: revenue.amount,
            })));

            setProductData(topSaleProduct.map(p => ({
                name: p.product.name,
                value: p.quantity
            })));

            setRecentOrders(orders.slice(0, 5).map(order => ({
                id: order.orderId,
                customer: order.fullName || order.email || 'Khách không xác định',
                date: new Date(order.orderDate).toLocaleDateString('vi-VN', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                }),
                total: order.totalAmount || 0,
                status: order.status || 'Không xác định'
            })));
        }
    }, [reports]);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                type: "spring",
                stiffness: 100
            }
        }
    };

    const getStatusInVietnamese = (status) => {
        switch (status) {
            case 'Completed': return 'Hoàn Thành';
            case 'Pending': return 'Đang chờ xử lý';
            case "Cancelled": return "Đã hủy";
            case 'Received': return 'Nhận hàng thành công';
            case 'Không xác định': return 'Không xác định';
            default: return status;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Completed': return 'text-green-800 bg-green-100';
            case 'Pending': return 'text-yellow-800 bg-yellow-100';
            case "Cancelled": return 'text-red-800 bg-red-100';
            case 'Received': return 'text-blue-800 bg-blue-100';
            default: return 'text-gray-800 bg-gray-100';
        }
    };

    return (
        <PageWrapper title="Thông tin chung">
        <>
            {reports === null ? (
                <Loading isLoading={false} />
            ) : (
                <div className="main-content">
                    <motion.div
                        className="dashboard-content"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        <motion.div
                            className="stats-container"
                            variants={itemVariants}
                        >
                            <motion.div
                                className="stat-card"
                                whileHover={{ y: -5, boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.1)" }}
                            >
                                <div className="stat-icon customers">
                                    <FaUsers />
                                </div>
                                <div className="stat-details">
                                    <h3>Khách hàng</h3>
                                    <p>{stats.totalCustomers.toLocaleString()}</p>
                                </div>
                            </motion.div>

                            <motion.div
                                className="stat-card"
                                whileHover={{ y: -5, boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.1)" }}
                            >
                                <div className="stat-icon orders">
                                    <FaShoppingCart />
                                </div>
                                <div className="stat-details">
                                    <h3>Đơn hàng hoàn tất</h3>
                                    <p>{stats.totalOrders.toLocaleString()}</p>
                                </div>
                            </motion.div>

                            <motion.div
                                className="stat-card"
                                whileHover={{ y: -5, boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.1)" }}
                            >
                                <div className="stat-icon products">
                                    <FaBoxOpen />
                                </div>
                                <div className="stat-details">
                                    <h3>Sản phẩm bán ra</h3>
                                    <p>{stats.totalProducts.toLocaleString()}</p>
                                </div>
                            </motion.div>

                            <motion.div
                                className="stat-card"
                                whileHover={{ y: -5, boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.1)" }}
                            >
                                <div className="stat-icon revenue">
                                    <FaMoneyBillWave />
                                </div>
                                <div className="stat-details">
                                    <h3>Doanh thu</h3>
                                    <p>{stats.totalRevenue.toLocaleString()} VND</p>
                                </div>
                            </motion.div>
                        </motion.div>

                        <div className="charts-container">
                            <motion.div
                                className="chart-card sales-chart bg-white rounded-lg shadow p-6"
                                variants={itemVariants}
                            >
                                <h3 className="text-lg font-semibold text-gray-800 mb-4">Doanh Thu Theo Tháng</h3>
                                <ResponsiveContainer width="100%" height={500}>
                                    <BarChart
                                        data={salesData}
                                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                        <XAxis
                                            dataKey="name"
                                            stroke="#6b7280"
                                            tick={{ fontSize: 12, fill: '#6b7280' }}
                                            interval={0}
                                        />
                                        <YAxis
                                            stroke="#6b7280"
                                            tick={{ fontSize: 12, fill: '#6b7280' }}
                                            tickFormatter={(value) => `${value.toLocaleString()} VND`}
                                            domain={[0, 'dataMax + 500000']}
                                            tickCount={6}
                                        />
                                        <Tooltip
                                            formatter={(value) => [`${value.toLocaleString()} VND`, 'Doanh thu']}
                                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                                        />
                                        <Legend
                                            wrapperStyle={{ fontSize: 12, color: '#6b7280', paddingTop: '5px' }}
                                            verticalAlign="bottom"
                                            height={20}
                                        />
                                        <Bar
                                            dataKey="sales"
                                            name="Doanh thu"
                                            className="transition-colors duration-500"
                                            fill={salesChartColor}
                                            radius={[4, 4, 0, 0]}
                                            barSize={30}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </motion.div>

                            <div className="small-charts">
                                <motion.div
                                    className="chart-card product-chart bg-white rounded-lg shadow p-6 max-h-[700px] " // Thêm max-h và overflow-y-auto
                                    variants={itemVariants}
                                >
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Sản Phẩm</h3>
                                    <ResponsiveContainer width="100%" height={500}>
                                        <BarChart
                                            data={productData}
                                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                                            layout="vertical"
                                        >
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                            <XAxis
                                                type="number"
                                                stroke="#6b7280"
                                                tick={{ fontSize: 12, fill: '#6b7280' }}
                                            />
                                            <YAxis
                                                dataKey="name"
                                                type="category"
                                                width={150} // Tăng chiều rộng để chứa tên dài
                                                stroke="#6b7280"
                                                tick={{ fontSize: 10, fill: '#6b7280' }}
                                                tickMargin={10} // Tăng khoảng cách giữa các nhãn
                                                interval={0} // Hiển thị tất cả nhãn
                                                tickFormatter={(value) => value.length > 15 ? `${value.substring(0, 15)}...` : value} // Cắt ngắn nhãn dài
                                            />
                                            <Tooltip
                                                formatter={(value) => [`${value} sản phẩm`, 'Số lượng']}
                                                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                                            />
                                            <Legend
                                                wrapperStyle={{ fontSize: 12, color: '#6b7280', paddingTop: '5px' }}
                                                verticalAlign="bottom"
                                                height={20}
                                            />
                                            <Bar
                                                dataKey="value"
                                                name="Số lượng"
                                                className="transition-colors duration-500"
                                                fill={productChartColor}
                                                radius={[0, 4, 4, 0]}
                                                barSize={20}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </motion.div>
                            </div>
                        </div>

                        <motion.div
                            className="recent-orders"
                            variants={itemVariants}
                        >
                            <div className="section-header">
                                <h3 className="text-lg font-semibold text-gray-800 mb-4" >Đơn Hàng Gần Đây</h3>
                                <motion.button
                                    className="view-all-btn"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => navigate('/manager/orders')}
                                >
                                    Xem Tất Cả
                                </motion.button>
                            </div>
                            <div className="orders-table">
                                <table>
                                    <thead>
                                    <tr>
                                        <th>Mã Đơn</th>
                                        <th>Khách Hàng</th>
                                        <th>Ngày Đặt</th>
                                        <th>Tổng Tiền</th>
                                        <th>Trạng Thái</th>
                                        <th>Hành Động</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {recentOrders.map((order) => (
                                        <motion.tr
                                            key={order.id}
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <td>#{order.id}</td>
                                            <td>{order.customer}</td>
                                            <td>{order.date}</td>
                                            <td>{order.total.toLocaleString()} VND</td>
                                            <td>
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(order.status)}`}>
                                                    {getStatusInVietnamese(order.status)}
                                                </span>
                                            </td>
                                            <td>
                                                <button
                                                    onClick={() =>
                                                        navigate(`/manager/order-detail/${order.id}`)
                                                    }
                                                    className="text-blue-600 hover:text-blue-900 mr-4"
                                                    title="Xem chi tiết"
                                                >
                                                    <Eye className="h-5 w-5" />
                                                </button>
                                            </td>
                                        </motion.tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            )}
        </>
        </PageWrapper>
    );
};

export default Dashboard;